/**
 * Crew personal-note extraction for the dispatch board.
 *
 * When "Send Schedule" goes out, the backend stamps the personal note a crew
 * member received onto the events it was sent with, as
 * `metadata.personalNotes = "{ [crewUserId]: { note, sentAt } }"` (a JSON
 * string — Neo4j cannot store nested maps as property values)
 * (Ray #6983: personal notes should be visible directly on Dispatch, not only
 * in the send dialog / the email).
 *
 * Metadata may arrive as a parsed object or a JSON string (Neo4j round-trip),
 * so extraction tolerates both and never throws — a malformed metadata value
 * must not break the board render.
 */

/** A personal note persisted by the send-schedule-to-crew comment mode. */
export interface CrewPersonalNote {
    note: string;
    /** ISO timestamp of when the schedule (with this note) was sent. */
    sentAt?: string;
}

/** Parse an unknown metadata value into an object, tolerating JSON strings. */
function parseMetadata(metadata: unknown): Record<string, any> | null {
    if (!metadata) return null;
    if (typeof metadata === 'object') return metadata as Record<string, any>;
    if (typeof metadata === 'string') {
        try {
            const parsed = JSON.parse(metadata);
            return parsed && typeof parsed === 'object' ? parsed : null;
        } catch {
            return null;
        }
    }
    return null;
}

/**
 * All personal notes on an event's metadata (keyed by crew user id), or null
 * when none exist.
 *
 * The backend persists `personalNotes` as a JSON string (Neo4j property
 * values must be primitives — CodeRabbit round 17), so the map is parsed
 * through the same tolerant path as the metadata itself.
 */
export function extractCrewPersonalNotes(metadata: unknown): Record<string, CrewPersonalNote> | null {
    const parsed = parseMetadata(metadata);
    const personalNotes = parseMetadata(parsed?.personalNotes);
    if (!personalNotes || typeof personalNotes !== 'object' || Array.isArray(personalNotes)) return null;
    return personalNotes as Record<string, CrewPersonalNote>;
}

/**
 * The personal note a specific crew member received for this event, or null.
 */
export function getCrewPersonalNote(
    metadata: unknown,
    userId: string | null | undefined,
): CrewPersonalNote | null {
    if (!userId) return null;
    const all = extractCrewPersonalNotes(metadata);
    const note = all?.[userId];
    if (!note || typeof note.note !== 'string' || !note.note.trim()) return null;
    return note;
}
